import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  InputAdornment,
  Grid,
  Alert,
  Divider,
  IconButton,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as ProductIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useArticlesCore } from '../../../../core/modules/articles';
import { Article } from '../../../../core/interfaces/article.interface';
import { useMasterCore } from '../../../../core/modules/master';
import CreateArticle from '../../../screens/dashboard/administration/articles/CreateArticle';

interface ProductSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (product: Article) => void;
  title: string;
  companyId?: number;
}

export const ProductSearchDialog: React.FC<ProductSearchDialogProps> = ({
  open,
  onClose,
  onSelect,
  title,
  companyId,
}) => {
  const { getArticle, getArticles, articles, postArticle, setArticle } = useArticlesCore();
  const { productTypes, productCategories, getTypes, getCategories } = useMasterCore();
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedTypeId, setSelectedTypeId] = useState<number | ''>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');
  // Solo creación completa (sin creación rápida)
  const [createArticleOpen, setCreateArticleOpen] = useState(false);
  const [replicateProduct, setReplicateProduct] = useState<Article | null>(null);

  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };

  const getProductImage = (product: Article) => {
    if (!product.files || product.files.length === 0) return null;
    const validFile = product.files.find((file) => file.URL);
    return validFile?.URL || null;
  };

  // Cargar productos al abrir el modal y limpiar al cerrar
  useEffect(() => {
    if (open && companyId) {
      // Cargar todos los productos de la compañía al abrir
      setLoading(true);
      // cargar tipos y categorías
      getTypes(companyId, 'product');
      getCategories(companyId, 'product');
      getArticles(
        companyId,
        { page: 1 }, // Sin filtros para traer todos
        () => {
          setLoading(false);
        },
        () => {
          setSearchError('Error al cargar productos');
          setLoading(false);
        }
      );
    } else if (!open) {
      setSearchTerm('');
      setSearchError(null);
      setSelectedTypeId('');
      setSelectedCategoryId('');
    }
  }, [open, companyId, getArticles]);

  // Limpiar errores cuando cambie el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim()) {
      // Si hay término de búsqueda, limpiar errores
      setSearchError(null);
    } else {
      // Si no hay término, asegurar que se muestren todos los productos
      // y limpiar errores de "no encontrados"
      if (searchError === 'No se encontraron productos con los criterios de búsqueda') {
        setSearchError(null);
      }
    }
  }, [searchTerm, searchError]);

  // Usar los productos del store (filtrados o todos)
  const { filteredProducts, productThumbnails } = useMemo(() => {
  const allProducts = articles || [];

    const filtered = allProducts.filter((product) => {
    const matchesSearch = searchTerm.trim()
      ? (
          product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.code?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : true;

    const matchesCategory = selectedCategoryId !== ''
      ? product.product_category_id === selectedCategoryId
      : true;

    const matchesType = selectedTypeId !== ''
      ? (() => {
          const category = productCategories.find((c) => c.id === product.product_category_id);
          return category ? (category as any).product_type_id === selectedTypeId : false;
        })()
      : true;

    return matchesSearch && matchesCategory && matchesType;
  });

    const thumbnails = new Map<number, string>();
    filtered.forEach((product) => {
      const imageUrl = getProductImage(product);
      if (imageUrl) {
        thumbnails.set(product.id, imageUrl);
      }
    });

    return { filteredProducts: filtered, productThumbnails: thumbnails };
  }, [
    articles,
    searchTerm,
    selectedCategoryId,
    selectedTypeId,
    productCategories,
  ]);

  // Categorías filtradas por tipo
  const availableCategories = selectedTypeId !== ''
    ? productCategories.filter((c) => (c as any).product_type_id === selectedTypeId)
    : productCategories;
  


  const handleProductSelect = (product: Article) => {
    onSelect(product);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: 600,
          zIndex: 1300,
        },
      }}
      sx={{
        zIndex: 1300,
        '& .MuiBackdrop-root': {
          zIndex: 1299,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ProductIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <Button onClick={onClose} size="small">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Alerta de copia exitosa */}
          {copySuccess && (
            <Alert severity="success" onClose={() => setCopySuccess(null)}>
              {copySuccess}
            </Alert>
          )}

          {/* Búsqueda y filtros */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Buscar Producto
            </Typography>
            
            {/* Alerta de error */}
            {searchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {searchError}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Buscar por código, nombre o descripción"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de producto"
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value as any)}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {productTypes.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Categoría"
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value as any)}
                >
                  <MenuItem value="">
                    <em>Todas</em>
                  </MenuItem>
                  {availableCategories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Box display="flex" gap={1} mt={2}>
              <Button variant="contained" size="small" onClick={() => setCreateArticleOpen(true)}>
                Crear artículo
              </Button>
            </Box>
          </Box>

          <Divider />

          {/* Lista de productos */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Productos disponibles ({filteredProducts.length})
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : filteredProducts.length === 0 ? (
              <Alert severity="info">
                No se encontraron productos con los criterios de búsqueda.
              </Alert>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {filteredProducts.map((product, index) => (
                  <React.Fragment key={product.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleProductSelect(product)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            variant={productThumbnails.has(product.id) ? 'rounded' : 'circular'}
                            src={productThumbnails.get(product.id)}
                            alt={product.name}
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: productThumbnails.has(product.id) ? 2 : '50%',
                              bgcolor: productThumbnails.has(product.id) ? 'transparent' : 'grey.200',
                            }}
                          >
                            {!productThumbnails.has(product.id) && <ProductIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body1" fontWeight="medium">
                                {product.code || 'Sin código'}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const replica: Article = {
                                    ...(product as Article),
                                    code: '',
                                    alternative_code: product.code,
                                  } as Article;
                                  setArticle(replica);
                                  setReplicateProduct(replica);
                                  setCreateArticleOpen(true);
                                }}
                                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', backgroundColor: 'action.hover' } }}
                                title="Replicar producto"
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {product.description}
                              </Typography>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" display="block">
                                    <strong>Marca:</strong> {product.brand}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" display="block">
                                    <strong>Dimensiones:</strong> {product.width_cm}x{product.length_cm}x{product.depth_cm} cm
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" display="block">
                                    <strong>Peso:</strong> {product.weight_kg} kg
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    
                    {index < filteredProducts.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancelar
        </Button>
      </DialogActions>
      {/* Crear artículo full y volver seleccionado */}
      <Dialog open={createArticleOpen} onClose={() => setCreateArticleOpen(false)} fullScreen>
        <CreateArticle
          embedded
          onClose={() => setCreateArticleOpen(false)}
          initialArticle={replicateProduct || undefined}
          onCreated={(article) => {
            onSelect(article);
            setCreateArticleOpen(false);
            onClose();
          }}
        />
      </Dialog>
    </Dialog>
  );
};
